<template>
  <div class="lib__form__fieldset-group">
    <fieldset class="lib__form__fieldset">
      <legend class="lib__form__legend">{{ $t('contact.contactType') }}</legend>
      <contact-type-selector :data-vv-as="$t('contact.contactType')"
                             v-model="value.contactType"></contact-type-selector>
    </fieldset>

    <contact-individual-form :show-birthdate="showBirthdate" :tooltips="tooltips" :ibanRequired="ibanRequired"
                             :value="value.contactIndividual" data-cy="contact-individual-form"
                             :with-iban-holder-name="withIbanHolderName"
                             v-if="value.contactType === 'individual'"></contact-individual-form>
    <contact-freelance-form :show-birthdate="showBirthdate" :isSmartMeterForm="isSmartMeterForm" :tooltips="tooltips"
                            :value="value.contactFreelance" :ibanRequired="ibanRequired"
                            :with-iban-holder-name="withIbanHolderName"
                            v-if="value.contactType === 'freelance'"></contact-freelance-form>
    <contact-company-form :show-birthdate="showBirthdate" :tooltips="tooltips" :isSmartMeterForm="isSmartMeterForm"
                          :value="value.contactCompany" v-if="value.contactType === 'company'"></contact-company-form>

    <fieldset class="lib__form__fieldset" v-if="showAddress">
      <legend class="lib__form__legend">{{ billingInfo ? $t('contact.billingAddress') : $t('contact.contactAddress')
        }}
      </legend>
      <RadioButton :item="1" data-cy="use-work-address-rdb" group="useWorksAddress" v-model="useWorksAddress">
        <span v-if="value.contactType === 'company'">
          {{ $t('contact.useCompanyAddress') }} :
          <p class="lib__text-gray-500 lib__no-margin">
            {{ value.contactCompany && value.contactCompany.address.street }} {{ value.contactCompany && value.contactCompany.address.streetNumber
            }} {{ value.contactCompany && value.contactCompany.address.streetBox }}
            <br />
            {{ value.contactCompany && value.contactCompany.address.zipCode }} {{ value.contactCompany && value.contactCompany.address.locality }}
          </p>
        </span>
        <span v-else-if="isConsumptionAddress">
          {{ $t('contact.useWorksAddress') }} :
          <p class="lib__text-gray-500 lib__no-margin">
            {{ consumptionAddress.streetName }} {{ consumptionAddress.houseNumber
            }} {{ consumptionAddress.complement || '' }} {{ consumptionAddress.complementBox || '' }}
            <br />
            {{ consumptionAddress.zipCode }} {{ consumptionAddress.cityName }}
          </p>
        </span>
        <span v-else>
          {{ $t('contact.useWorksAddress') }} :
          <p class="lib__text-gray-500 lib__no-margin">
            <template v-if="!addressPoint.isMapLocation">{{ addressPoint.streetSubCity && addressPoint.streetSubCity.name }} {{ addressPoint.houseNumber
              }}</template>
            <br />
            {{ addressPoint.subCity.zipCode }} {{ addressPoint.subCity.cityName }}
          </p>
        </span>
      </RadioButton>
      <RadioButton :item="0" group="useWorksAddress" v-model="useWorksAddress">{{ $t('contact.otherAddress') }}
      </RadioButton>
      <address-form :value="value.otherAddress" v-if="!value.useWorksAddress"></address-form>
      <hr v-if="!hideOtherContact && !isProsumerDeclarationForm"/>

      <ContactPeopleForm :show-send-offer-copy="showSendOfferCopy" v-if="!hideOtherContact && !(isProsumerDeclarationForm && value.contactType === 'company')"
                           v-model="value.otherContacts" :showCivility="showCivility"
                           :max-contact-count="maxOtherContactCount"></ContactPeopleForm>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { contactService } from '@/services'
import { ContactInfo, ContactType, AddressPoint, ConsumptionAddress } from '@/models'
import ContactTypeSelector from '@/components/inputs/Contact/ContactTypeSelector.vue'
import ContactIndividualForm from '@/components/inputs/Contact/ContactIndividualForm.vue'
import ContactFreelanceForm from '@/components/inputs/Contact/ContactFreelanceForm.vue'
import ContactCompanyForm from '@/components/inputs/Contact/ContactCompanyForm.vue'
import ContactPeopleForm from '@/components/inputs/Contact/ContactPeopleForm.vue'
import AddressForm from '@/components/inputs/Contact/AddressForm.vue'

@Component({
  name: 'contact-info-form',
  components: {
    ContactTypeSelector,
    ContactIndividualForm,
    ContactFreelanceForm,
    ContactCompanyForm,
    ContactPeopleForm,
    AddressForm
  }
})
export default class ContactForm extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true })
  public readonly value!: ContactInfo
  @Prop({ type: Object, required: true })
  public address!: AddressPoint | ConsumptionAddress
  @Prop({ type: Boolean, required: false, default: false })
  public hideOtherContact!: boolean
  @Prop({ type: Boolean, required: false, default: true })
  public readonly showSendOfferCopy!: boolean
  @Prop({ type: Object, required: false, default: () => ({}) })
  public readonly tooltips!: { [key: string]: string | undefined }
  @Prop({ type: Boolean, required: false, default: false })
  public readonly isSmartMeterForm!: boolean

  @Prop({ type: Boolean, required: false, default: false })
  public readonly isProsumerDeclarationForm!: boolean
  @Prop({ type: Boolean, required: false, default: true })
  public readonly showBirthdate!: boolean
  @Prop({ type: Boolean, required: false, default: false })
  public readonly ibanRequired!: boolean
  @Prop({ type: Boolean, required: false, default: true })
  public readonly billingInfo!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly withIbanHolderName!: boolean
  @Prop({ required: false, default: null }) public readonly maxOtherContactCount!: number | null
  @Prop({ type: Boolean, required: false, default: false }) public readonly showCivility!: boolean
  //#endregion

  //#region [Computed]
  public get isFilled(): boolean {
    if (!this.value.useWorksAddress && this.value.otherAddress == null) {
      return false
    }

    return this.showAddress
  }

  public get isConsumptionAddress(): boolean {
    return (this.address as ConsumptionAddress).cityName !== undefined
  }

  public get consumptionAddress(): ConsumptionAddress{
    return (this.address as ConsumptionAddress)
  }

  public get addressPoint(): AddressPoint{
    return (this.address as AddressPoint)
  }

  public get showAddress(): boolean {
    switch (this.value.contactType) {
      case null:
        return false
      case 'individual':
        return this.value.contactIndividual != null
      case 'freelance':
        return this.value.contactFreelance != null
      case 'company':
        const company = this.value.contactCompany
        return (
          company !== null &&
          !!company.address.street &&
          !!company.address.streetNumber &&
          !!company.address.zipCode &&
          !!company.address.locality &&
          !!company.address.country
        )
      default:
        return false
    }
  }

  public get useWorksAddress(): number {
    return this.value.useWorksAddress ? 1 : 0
  }

  public set useWorksAddress(value: number) {
    this.value.useWorksAddress = !!value
  }

  //#endregion

  //#region [Watch]
  @Watch('value.contactType')
  public onContactTypeChanged(value: ContactType) {
    switch (value) {
      case 'individual':
        if (this.value.contactIndividual == null) {
          this.value.contactIndividual = contactService.createContactIndividual()
        }
        break
      case 'freelance':
        if (this.value.contactFreelance == null) {
          this.value.contactFreelance = contactService.createContactFreelance()
        }
        break
      case 'company':
        if (this.value.contactCompany == null) {
          this.value.contactCompany = contactService.createContactCompany()
        }
        break
      default:
        break
    }
  }

  @Watch('value.useWorksAddress')
  public onUseWorksAddressChanged(value: boolean) {
    if (value && this.value.otherAddress !== null) {
      this.value.otherAddress = null
    } else if (!value && this.value.otherAddress === null) {
      this.value.otherAddress = contactService.createContactAddress()
    }
  }

  //#endregion

  //#region [Method]
  public setUseWorksAddress(value: boolean) {
    this.value.useWorksAddress = value
  }

  //#endregion
}
</script>
